import { Box, Text } from '@pancakeswap/uikit'
import { useCallback, useState } from 'react'
import useSWR from 'swr'
import { getWalletClient } from 'wagmi/actions'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { isUserRejection } from 'views/PoolTrade/plan'
import { PrimaryCta } from 'views/TokenPages/ui'
import { AckCompetition, AckError, AckStatus, ackErrorText, ackTypedData, getAck, postAck } from '../ack'
import { fetcher } from '../config'
import { useContractBinding } from '../contract'
import { useV3Wallet } from '../hooks'

/** What the acknowledgement is about: the chain and the contract the signature is bound to come from the API's own config, never from this page's guess. */
function useChainConfig(apiUrl: string) {
  return useSWR<{ chainId: number; contract: string }>(`${apiUrl}/config`, fetcher, { revalidateOnFocus: false, dedupingInterval: 60_000 })
}

export type AckState = 'no_wallet' | 'loading' | 'needed' | 'signing' | 'done' | 'unavailable'

/**
 * The one-time acknowledgement of a competition's rules, per wallet. `state` is what the trade panel needs: done unlocks it. The server is the only record
 * (GET tells whether this wallet signed); nothing is kept in the browser, so a new device or a cleared cache asks nothing twice and forgets nothing.
 */
export function useRulesAck(c: AckCompetition | undefined, wallet: string | undefined) {
  // The page's own contract (V4, or V3 on /legacy pages): its API stores the acknowledgement and its config names the contract the signature is bound
  // to. Until 2026-10-03 this was always the V3 API: a V4 page signed its competition under the V3 domain and the V3 endpoint rebuilt the V3
  // competition with the same id, so every wallet got signer_mismatch on /trading-competition/0.
  const { apiUrl, chainId } = useContractBinding()
  const cfg = useChainConfig(apiUrl)
  const key = c && wallet ? `ack:${apiUrl}:${c.id}:${wallet.toLowerCase()}` : null
  const status = useSWR<AckStatus>(key, () => getAck(apiUrl, (c as AckCompetition).id, (wallet as string).toLowerCase()), { revalidateOnFocus: false, shouldRetryOnError: false })
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sign = useCallback(async () => {
    if (!c || !wallet || !cfg.data) return false
    setSigning(true)
    setError(null)
    try {
      const w = await getWalletClient({ chainId })
      if (!w) throw new AckError('wallet')
      const timestamp = Math.floor(Date.now() / 1000)
      const typed = ackTypedData(c, { chainId: cfg.data.chainId, contract: cfg.data.contract }, wallet, timestamp)
      const signature = await w.signTypedData({ account: w.account, ...typed })
      await postAck(apiUrl, c.id, { wallet: wallet.toLowerCase(), timestamp, signature })
      await status.mutate()
      return true
    } catch (e) {
      const rejected = isUserRejection(e)
      setError(rejected ? 'Cancelled in the wallet. Nothing was signed.' : e instanceof AckError ? ackErrorText(e.code) : 'The wallet could not sign. Try again.')
      return false
    } finally {
      setSigning(false)
    }
  }, [c, wallet, cfg.data, status, apiUrl, chainId])

  let state: AckState = 'loading'
  if (!wallet) state = 'no_wallet'
  else if (signing) state = 'signing'
  else if (status.data?.acknowledged) state = 'done'
  else if (status.error || cfg.error) state = 'unavailable'
  else if (status.data) state = 'needed'
  return { state, sign, error }
}

/** Shown instead of the trade form until the wallet has acknowledged. Not an eligibility condition, and it says so. */
export default function AckGate({ state, onSign, error }: { state: AckState; onSign: () => Promise<boolean>; error: string | null }) {
  const { wrongChain, switching, switchToBsc } = useV3Wallet()
  const [ticked, setTicked] = useState(false)
  const big = { minHeight: 52, width: '100%' } as const
  return (
    <Box data-testid="ack-gate" data-state={state}>
      <Text bold>Before your first trade here</Text>
      <Text fontSize="14px" color="textSubtle" mt="4px">
        Trades made through this panel go through the competition pool only. Sign once, for free (no gas), that you understand it. Trades you make
        elsewhere through the same pool still count: this is not a condition to take part.
      </Text>
      {state === 'no_wallet' && <Box mt="16px"><ConnectWalletButton style={big} data-testid="ack-connect">Connect wallet</ConnectWalletButton></Box>}
      {state === 'loading' && <Text mt="16px" color="textSubtle" data-testid="ack-loading">Checking your acknowledgement…</Text>}
      {state === 'unavailable' && <Text mt="16px" color="warning" role="status" data-testid="ack-unavailable">The acknowledgement service is not answering. Trading stays closed here until it does.</Text>}
      {(state === 'needed' || state === 'signing') && (
        <Box mt="16px">
          <label htmlFor="ack-check" style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: 44, fontSize: 15, cursor: 'pointer' }}>
            <input id="ack-check" type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="ack-check" style={{ width: 22, height: 22, flex: 'none' }} />
            I understand only trades through this pool count
          </label>
          <Box mt="12px">
            {wrongChain ? (
              <PrimaryCta as="button" type="button" onClick={switchToBsc} disabled={switching} style={big} data-testid="ack-switch">{switching ? 'Switching…' : 'Switch to BNB Smart Chain'}</PrimaryCta>
            ) : (
              <PrimaryCta as="button" type="button" onClick={onSign} disabled={!ticked || state === 'signing'} aria-disabled={!ticked || state === 'signing'} style={{ ...big, opacity: !ticked || state === 'signing' ? 0.6 : 1 }} data-testid="ack-sign">
                {state === 'signing' ? 'Confirm in your wallet…' : 'Sign (free, no gas)'}
              </PrimaryCta>
            )}
          </Box>
        </Box>
      )}
      {error && <Text mt="12px" color="failure" role="alert" data-testid="ack-error">{error}</Text>}
    </Box>
  )
}

